import type {
    DriveFile,
    GoogleSheetsSpreadsheet,
    GoogleSheetsValueRange,
    GoogleSheetsBatchUpdateRequest,
    GoogleSheetsBatchUpdateResponse,
} from './types'

export async function generateDriveFileIds(
    accessToken: string,
    count: number = 1,
): Promise<string[]> {
    const url = new URL('https://www.googleapis.com/drive/v3/files/generateIds')
    url.searchParams.set('count', count.toString())
    url.searchParams.set('space', 'drive')

    const response = await fetch(url, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    })

    if (!response.ok) {
        throw new Error(
            `Drive API error: ${response.status} ${response.statusText}`,
        )
    }

    const data = await response.json()
    return data.ids ?? []
}

export async function uploadPdfToDrive(
    accessToken: string,
    params: {
        folderId: string
        name: string
        data: Blob | ArrayBuffer | Uint8Array | Buffer
        id?: string
    },
): Promise<DriveFile> {
    const url = new URL('https://www.googleapis.com/upload/drive/v3/files')
    url.searchParams.set('uploadType', 'multipart')
    url.searchParams.set(
        'fields',
        'id,name,mimeType,parents,webViewLink,webContentLink',
    )
    url.searchParams.set('supportsAllDrives', 'true')

    const metadata: {
        name: string
        parents: string[]
        mimeType: string
        id?: string
    } = {
        name: params.name,
        parents: [params.folderId],
        mimeType: 'application/pdf',
    }

    if (params.id) {
        metadata.id = params.id
    }

    // Convert all input types to Buffer for Node.js compatibility
    let fileBuffer: Buffer
    if (Buffer.isBuffer(params.data)) {
        fileBuffer = params.data
    } else if (params.data instanceof Uint8Array) {
        fileBuffer = Buffer.from(params.data)
    } else if (params.data instanceof ArrayBuffer) {
        fileBuffer = Buffer.from(params.data)
    } else if (params.data instanceof Blob) {
        // Convert Blob to Buffer via ArrayBuffer
        const arrayBuffer = await params.data.arrayBuffer()
        fileBuffer = Buffer.from(arrayBuffer)
    } else {
        throw new Error('Unsupported data type')
    }

    const boundary = `multipart_${Math.random().toString(36).slice(2)}`
    const delimiter = `--${boundary}`
    const closeDelimiter = `--${boundary}--`

    // Build multipart body as Buffer for Node.js fetch compatibility
    const metadataPart = Buffer.from(
        `${delimiter}\r\n` +
            'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
            JSON.stringify(metadata) +
            `\r\n${delimiter}\r\n` +
            'Content-Type: application/pdf\r\n\r\n',
        'utf-8',
    )

    const closingPart = Buffer.from(`\r\n${closeDelimiter}\r\n`, 'utf-8')

    const body = Buffer.concat([metadataPart, fileBuffer, closingPart])

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': `multipart/related; boundary=${boundary}`,
        },
        body,
    })

    if (!response.ok) {
        const errorText = await response.text().catch(() => '')
        throw new Error(
            `Drive API error: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`,
        )
    }
    const responseData = await response.json()
    return responseData as DriveFile
}

export async function findDriveFolderByName(
    accessToken: string,
    params: {
        folderName: string
        parentId?: string
    },
): Promise<DriveFile | null> {
    const url = new URL('https://www.googleapis.com/drive/v3/files')

    // Build search query based on whether parentId is provided
    const queryParts = [
        `name='${params.folderName}'`,
        `mimeType='application/vnd.google-apps.folder'`,
        'trashed=false',
    ]

    if (params.parentId) {
        queryParts.push(`'${params.parentId}' in parents`)
    }

    url.searchParams.set('q', queryParts.join(' and '))
    url.searchParams.set(
        'fields',
        'files(id,name,mimeType,parents,webViewLink,webContentLink)',
    )
    url.searchParams.set('spaces', 'drive')
    url.searchParams.set('supportsAllDrives', 'true')
    url.searchParams.set('includeItemsFromAllDrives', 'true')

    const response = await fetch(url, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    })

    if (!response.ok) {
        throw new Error(
            `Drive API error: ${response.status} ${response.statusText}`,
        )
    }

    const data = await response.json()
    const folders = data.files ?? []

    return folders.length > 0 ? (folders[0] as DriveFile) : null
}

export async function createDriveFolder(
    accessToken: string,
    params: {
        folderName: string
        parentId?: string
    },
): Promise<DriveFile> {
    const url = new URL('https://www.googleapis.com/drive/v3/files')
    url.searchParams.set(
        'fields',
        'id,name,mimeType,parents,webViewLink,webContentLink',
    )
    url.searchParams.set('supportsAllDrives', 'true')

    const metadata: Partial<DriveFile> = {
        name: params.folderName,
        mimeType: 'application/vnd.google-apps.folder',
    }

    if (params.parentId) {
        metadata.parents = [params.parentId]
    }

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(metadata),
    })

    if (!response.ok) {
        throw new Error(
            `Drive API error: ${response.status} ${response.statusText}`,
        )
    }

    return (await response.json()) as DriveFile
}

export async function getOrCreateDriveFolder(
    accessToken: string,
    params: {
        folderName: string
        parentId?: string
    },
): Promise<DriveFile> {
    const existingFolder = await findDriveFolderByName(accessToken, params)
    if (existingFolder) {
        return existingFolder
    }
    return await createDriveFolder(accessToken, params)
}

export async function getDriveFileById(
    accessToken: string,
    fileId: string,
): Promise<DriveFile> {
    const url = new URL(`https://www.googleapis.com/drive/v3/files/${fileId}`)
    url.searchParams.set(
        'fields',
        'id,name,mimeType,parents,webViewLink,webContentLink,trashed',
    )
    url.searchParams.set('supportsAllDrives', 'true')

    const response = await fetch(url, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    })

    if (!response.ok) {
        throw new Error(
            `Drive API error: ${response.status} ${response.statusText}`,
        )
    }

    return (await response.json()) as DriveFile
}

export async function downloadDriveFile(
    accessToken: string,
    fileId: string,
): Promise<{ data: Blob; mimeType: string; filename: string }> {
    const retryDelays = [2000, 3000, 5000]
    let lastError: Error | null = null

    for (let attempt = 0; attempt <= retryDelays.length; attempt++) {
        try {
            // Download the file using alt=media parameter
            const downloadUrl = new URL(
                `https://www.googleapis.com/drive/v3/files/${fileId}`,
            )
            downloadUrl.searchParams.set('alt', 'media')
            downloadUrl.searchParams.set('supportsAllDrives', 'true')

            const downloadResponse = await fetch(downloadUrl, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            })

            if (!downloadResponse.ok) {
                // Only retry on 404 errors
                if (
                    downloadResponse.status === 404 &&
                    attempt < retryDelays.length
                ) {
                    const delay = retryDelays[attempt]
                    await new Promise((resolve) => setTimeout(resolve, delay))
                    continue
                }
                throw new Error(
                    `Drive API error: ${downloadResponse.status} ${downloadResponse.statusText}`,
                )
            }

            const blob = await downloadResponse.blob()
            const mimeType =
                downloadResponse.headers.get('Content-Type') ||
                'application/pdf'

            // Try to extract filename from Content-Disposition header, fallback to default
            let filename = 'downloaded-file'
            const contentDisposition = downloadResponse.headers.get(
                'Content-Disposition',
            )
            if (contentDisposition) {
                const filenameMatch =
                    contentDisposition.match(/filename="?([^"]+)"?/i)
                if (filenameMatch) {
                    filename = filenameMatch[1]
                }
            }

            return {
                data: blob,
                mimeType,
                filename,
            }
        } catch (error) {
            lastError =
                error instanceof Error ? error : new Error(String(error))
            // Only retry on 404 errors, not other errors
            if (
                attempt < retryDelays.length &&
                (lastError.message.includes('404') ||
                    lastError.message.includes('Drive API error: 404'))
            ) {
                const delay = retryDelays[attempt]
                await new Promise((resolve) => setTimeout(resolve, delay))
                continue
            }
            throw lastError
        }
    }

    // This should never be reached, but TypeScript needs it
    throw lastError || new Error('Failed to download file after retries')
}

export async function createGoogleSheetsSpreadsheet(
    accessToken: string,
    params: {
        title: string
        parentId?: string
    },
): Promise<GoogleSheetsSpreadsheet> {
    const url = new URL('https://sheets.googleapis.com/v4/spreadsheets')

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            properties: {
                title: params.title,
                locale: 'en_US',
                autoRecalc: 'ON_CHANGE',
                timeZone: 'America/New_York',
            },
        }),
    })

    if (!response.ok) {
        throw new Error(
            `Google Sheets API error: ${response.status} ${response.statusText}`,
        )
    }

    return (await response.json()) as GoogleSheetsSpreadsheet
}

export async function batchUpdateGoogleSheets(
    accessToken: string,
    spreadsheetId: string,
    request: GoogleSheetsBatchUpdateRequest,
): Promise<GoogleSheetsBatchUpdateResponse> {
    const url = new URL(
        `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`,
    )

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
    })

    if (!response.ok) {
        throw new Error(
            `Google Sheets API error: ${response.status} ${response.statusText}`,
        )
    }

    return (await response.json()) as GoogleSheetsBatchUpdateResponse
}

export async function updateGoogleSheetsValues(
    accessToken: string,
    spreadsheetId: string,
    range: string,
    values: GoogleSheetsValueRange['values'],
): Promise<void> {
    const url = new URL(
        `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(
            range,
        )}?valueInputOption=USER_ENTERED`,
    )

    const response = await fetch(url, {
        method: 'PUT',
        headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            values,
        }),
    })

    if (!response.ok) {
        throw new Error(
            `Google Sheets API error: ${response.status} ${response.statusText}`,
        )
    }
}

export async function moveFileToFolder(
    accessToken: string,
    fileId: string,
    folderId: string,
): Promise<void> {
    const url = new URL(`https://www.googleapis.com/drive/v3/files/${fileId}`)
    url.searchParams.set('addParents', folderId)
    url.searchParams.set('fields', 'id,name,parents')
    url.searchParams.set('supportsAllDrives', 'true')

    const response = await fetch(url, {
        method: 'PATCH',
        headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            addParents: folderId,
        }),
    })

    if (!response.ok) {
        throw new Error(
            `Drive API error: ${response.status} ${response.statusText}`,
        )
    }
}
